import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import { Info, KeyRound, Mail, Check, Trash2 } from "lucide-react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { claimSessionTransition, endSessionAndSignIn, shouldEndSession } from "../lib/sessionExpiry";
import { useAuth } from "../context/AuthContext";
import { clearMoreForUser } from "../lib/moreMemory";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { AccessibilityMenu } from "../components/AccessibilityMenu";
import { AccountMenu } from "../components/AccountMenu";
import { NotificationsBell } from "../components/NotificationsBell";
import { TopBar, Field, SecondaryAction } from "../components/kit";
import {
  CONFIRM_ITS_YOU,
  DELETE_BACK,
  DELETE_EYEBROW,
  DELETE_BILLING_STOP_FAILED,
  DELETE_FAILED,
  DELETE_KEEP,
  DELETE_TITLE,
  DELETE_VERB,
  DELETE_VERB_BUSY,
  EMAIL_ME_A_LINK,
  GUARDIAN_11_TO_17,
  PASSKEY_CONFIRMED,
  PASSWORD_HELPER,
  PASSWORD_LABEL,
  USE_A_PASSKEY,
  WHAT_GOES_LABEL,
  WHAT_STAYS_LABEL,
  WHAT_STAYS_LINES,
  WHEN_LABEL,
  WHEN_LINES,
  WRONG_PASSWORD,
  coachPlayersLine,
  confirmMethods,
  deleteLede,
  guardianUnder11Lead,
  guardianUnder11Line,
  linkSent,
  whatGoesLines,
} from "../lib/deleteAccount";

/**
 * Delete my account: the confirm step (#1032, #502 builds 2 and 3, D204.2).
 *
 * A full page rather than an in-page fold, because it has to say three
 * things before it asks for anything: what is deleted, what stays, and when
 * (D191.1). `SettingsDeleteAccount.dc.html` is the drawing; every string is
 * in `lib/deleteAccount.js`.
 *
 * HOW THE PERSON CONFIRMS (D199.2: "however they sign in"), decided from the
 * facts `GET /auth/me` carries (`has_password`, `passkey_count`):
 *
 *   - a passkey on the account: "Use a passkey" comes first. It runs the
 *     signed-in re-authentication ceremony (`/auth/passkey/reauth/*`, which
 *     issues no session) and keeps the challenge id it answers with.
 *   - a usable password: the password box, under the passkey button when the
 *     account has both. (Accounts made passkey-first before #1032 still read
 *     as having a password, so a passkey is always offered when one exists.)
 *   - neither: "Email me a confirm link". The emailed link comes back here as
 *     `?confirm=<token>`; opening it deletes nothing, it only makes the verb
 *     live, and the token goes to the server on the press.
 *
 * The verb is at half strength and `aria-disabled` until a proof is in hand
 * (D108's disabled look for a filled button), and no gold appears anywhere
 * on the step (D55, D73): its one action is destructive.
 *
 * FAILURES STAY ON THE PAGE (D57, D109): never a toast. A wrong password is
 * the inline `delete-account-password-error` and nothing else changes; any
 * other failure is `delete-account-error`. While the delete runs, the verb,
 * Keep, Back and the account menu are all inert.
 *
 * On success the session is gone server-side. This clears the in-memory user
 * and leaves the SPA with a full page load, the way `signOut.js` does, so
 * nothing from the deleted account survives in memory, and the 401s that
 * start arriving are not read as an expired session.
 */

const ALERT = "var(--nw-alert)";

function Callout({ testId, children, tone = "alert" }) {
  return (
    <div
      data-testid={testId}
      role={tone === "alert" ? "alert" : undefined}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "10px 12px",
        background: "var(--nw-line-faint)",
        borderLeft: tone === "alert" ? `3px solid ${ALERT}` : undefined,
        border: tone === "alert" ? undefined : "1px solid var(--nw-line)",
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        color: "var(--nw-ink)",
      }}
    >
      {tone === "alert" && (
        <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
          <Info size={16} color="#B42318" aria-hidden="true" />
        </span>
      )}
      {tone === "ok" && (
        <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
          <Check size={16} style={{ color: "var(--nw-ok)" }} aria-hidden="true" />
        </span>
      )}
      <span>{children}</span>
    </div>
  );
}

function ListCard({ testId, label, lines }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "var(--nw-space-card)",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--nw-ink-muted)",
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {lines.map((line) => (
          <div key={line} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span
              aria-hidden="true"
              style={{ width: 6, height: 6, marginTop: 8, borderRadius: 999, background: "var(--nw-navy)", flexShrink: 0 }}
            />
            <span style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)" }}>{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A passkey ceremony the person dismissed is not a failure worth a line. */
const isDismissed = (e) => e?.name === "NotAllowedError" || e?.name === "AbortError";

export default function DeleteAccountPage() {
  const { setActive: setChromeless } = useChromelessOverride();
  const { user, clearSession } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const confirmToken = params.get("confirm") || "";

  // The facts come from a fresh `GET /auth/me`, not the context user: login
  // and the passkey routes build their own bodies, which do not carry them.
  const [facts, setFacts] = useState(null);
  const [factsFailed, setFactsFailed] = useState(false);
  const [password, setPassword] = useState("");
  const [passkeyProof, setPasskeyProof] = useState(null);
  const [linkToken, setLinkToken] = useState(confirmToken);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkWasSent, setLinkWasSent] = useState(false);
  const [methodError, setMethodError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The confirm token is in state now; take it out of the address bar (and so
  // out of history, referrers and a Back button that would re-arm the verb),
  // keeping the navigation state.
  useEffect(() => {
    if (confirmToken) nav(location.pathname, { replace: true, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFacts = useCallback(async () => {
    setFactsFailed(false);
    try {
      const { data } = await api.get("/auth/me");
      if (mountedRef.current) setFacts(data);
    } catch (e) {
      // `/auth/me` is exempt from the shared 401 handler (it is the ordinary
      // anonymous answer on public pages), so a dead session is read here:
      // "you're still signed in" would be false.
      if (e?.response?.status === 401 && endSessionAndSignIn()) return;
      if (mountedRef.current) setFactsFailed(true);
    }
  }, []);

  useEffect(() => {
    loadFacts();
  }, [loadFacts]);

  const email = facts?.email || user?.email || "";
  const methods = facts ? confirmMethods(facts) : { passkey: false, password: false, link: false };

  const hasProof = Boolean(passkeyProof) || Boolean(linkToken) || (methods.password && password.length > 0);
  const live = hasProof && !deleting && facts !== null;

  const startPasskey = async () => {
    if (passkeyBusy || deleting) return;
    setPasskeyBusy(true);
    setMethodError("");
    try {
      const { data: options } = await api.post("/auth/passkey/reauth/options", {});
      const assertion = await startAuthentication({ optionsJSON: options });
      const { data } = await api.post("/auth/passkey/reauth/verify", assertion);
      if (mountedRef.current && data?.challengeId) setPasskeyProof({ challengeId: data.challengeId });
    } catch (e) {
      if (mountedRef.current && !isDismissed(e)) {
        setMethodError(formatApiErrorDetail(e.response?.data?.detail) || DELETE_FAILED);
      }
    }
    if (mountedRef.current) setPasskeyBusy(false);
  };

  const requestLink = async () => {
    if (linkBusy || deleting) return;
    setLinkBusy(true);
    setMethodError("");
    try {
      await api.post("/auth/delete-account/link", {});
      if (mountedRef.current) setLinkWasSent(true);
    } catch (e) {
      if (mountedRef.current) setMethodError(formatApiErrorDetail(e.response?.data?.detail) || DELETE_FAILED);
    }
    if (mountedRef.current) setLinkBusy(false);
  };

  const proofBody = () => {
    if (passkeyProof) return { proof: { kind: "passkey", challengeId: passkeyProof.challengeId } };
    if (linkToken) return { proof: { kind: "link", token: linkToken } };
    return { proof: { kind: "password", password } };
  };

  const confirm = async () => {
    if (!live) return;
    setDeleting(true);
    setError("");
    setPasswordError("");
    // Claimed before the request: the moment the server ends the session,
    // everything this page still has in flight comes back 401, and none of
    // those may send the person to "your session ended" (see signOut.js).
    const release = claimSessionTransition();
    try {
      await api.post("/auth/delete-account", proofBody());
    } catch (e) {
      release();
      if (!mountedRef.current) return;
      const status = e.response?.status;
      const code = e.response?.data?.code;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (status === 401 && code === "delete_wrong_password") {
        setPasswordError(WRONG_PASSWORD);
      } else if (shouldEndSession(e, API)) {
        // A 401 with none of the delete codes is `requireUser` saying this
        // session is already gone (logged out elsewhere, expired). "You're
        // still signed in" would be false: take the ordinary sign-in
        // redirect, now the latch is back.
        if (endSessionAndSignIn()) return;
        setError(DELETE_FAILED);
      } else if (status === 502 && code === "delete_billing_stop_failed") {
        // Stripe could not be reached, so billing was not stopped and nothing was deleted (#1566).
        setError(DELETE_BILLING_STOP_FAILED);
      } else if (status >= 400 && status < 500 && detail) {
        // The server's own line for a refusal (429 too many attempts,
        // delete_proof_invalid, delete_confirm_method, the demo account).
        // Only a network error or a 5xx is "nothing has changed, check your
        // connection".
        setError(detail);
      } else {
        setError(DELETE_FAILED);
      }
      // A proof the server answered is spent or dead; one it never heard
      // (no response) may still be good, so only that one is kept.
      if (e.response) {
        setPasskeyProof(null);
        if (linkToken) setLinkToken("");
      }
      setDeleting(false);
      return;
    }
    // #1127 (Andreas's ruling): the "More" choice survives sign-out, and
    // goes only with the account.
    clearMoreForUser(user?.id);
    clearSession();
    window.location.assign("/account-deleted");
  };

  const backToSettings = () => {
    if (deleting) return;
    // Drop any proof in hand BEFORE leaving. react-router 7 navigates inside
    // startTransition, so this step stays mounted until the new route
    // commits; a fast Back cancels the navigation and lands on this same
    // instance, which would still hold the token and arm the verb.
    setLinkToken("");
    setPasskeyProof(null);
    nav("/settings");
  };

  const ageBand = facts?.age_band;
  const coachCount = facts?.role === "coach" ? facts.active_player_count : null;
  const playerName = facts?.display_name || user?.display_name || "";

  const verbStyle = {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 48,
    background: ALERT,
    color: "#FFFFFF",
    border: "none",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    fontWeight: 700,
    fontSize: 15,
    borderRadius: 10,
    opacity: live ? 1 : 0.45,
    cursor: live ? "pointer" : "not-allowed",
  };

  return (
    <div data-testid="delete-account-page">
      <span data-testid="delete-account-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />
      <TopBar
        variant="navy"
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <span {...(deleting ? { inert: true } : {})} style={{ display: "contents" }} data-testid="delete-account-menus">
            <NotificationsBell compact />
            <AccessibilityMenu compact />
            <AccountMenu />
          </span>
        }
        data-testid="delete-account-topbar"
      />

      <div className="flex flex-col" style={{ gap: "var(--nw-space-stack)", padding: "22px 20px 20px" }}>
        <SecondaryAction
          variant="link"
          onClick={backToSettings}
          aria-disabled={deleting || undefined}
          data-testid="delete-account-back"
          style={{
            alignSelf: "flex-start",
            justifyContent: "flex-start",
            fontFamily: "inherit",
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--nw-ink-muted)",
            textDecoration: "underline",
            cursor: deleting ? "not-allowed" : "pointer",
          }}
        >
          {DELETE_BACK}
        </SecondaryAction>

        <div className="flex flex-col" style={{ gap: 8 }}>
          <div className="nw-eyebrow">{DELETE_EYEBROW}</div>
          <h1 className="nw-title-lg" style={{ margin: 0 }}>
            {DELETE_TITLE}
          </h1>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-lede)" }} data-testid="delete-account-lede">
            {deleteLede(email)}
          </p>
        </div>

        <ListCard testId="delete-account-what-goes" label={WHAT_GOES_LABEL} lines={whatGoesLines(email)} />
        <ListCard testId="delete-account-what-stays" label={WHAT_STAYS_LABEL} lines={WHAT_STAYS_LINES} />
        <ListCard testId="delete-account-when" label={WHEN_LABEL} lines={WHEN_LINES} />

        {ageBand === "under_11" && (
          <>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-lede)" }} data-testid="delete-account-guardian-lead">
              {guardianUnder11Lead(playerName)}
            </p>
            <div
              data-testid="delete-account-guardian"
              style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", padding: "10px 12px", background: "var(--nw-line-faint)", borderRadius: 10 }}
            >
              {guardianUnder11Line(playerName)}
            </div>
          </>
        )}
        {ageBand === "11_to_17" && (
          <div
            data-testid="delete-account-guardian"
            style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", padding: "10px 12px", background: "var(--nw-line-faint)", borderRadius: 10 }}
          >
            {GUARDIAN_11_TO_17}
          </div>
        )}
        {coachCount > 0 && (
          <div
            data-testid="delete-account-coach-players"
            style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", padding: "10px 12px", background: "var(--nw-line-faint)", borderRadius: 10 }}
          >
            {coachPlayersLine(coachCount)}
          </div>
        )}

        {factsFailed && <Callout testId="delete-account-error">{DELETE_FAILED}</Callout>}

        {(methods.passkey || (methods.link && !linkToken)) && (
          <div className="flex flex-col" style={{ gap: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>{CONFIRM_ITS_YOU}</div>

            {methods.passkey &&
              (passkeyProof ? (
                <Callout testId="delete-account-confirmed" tone="ok">
                  {PASSKEY_CONFIRMED}
                </Callout>
              ) : (
                <SecondaryAction
                  variant="navy-outline"
                  onClick={startPasskey}
                  disabled={passkeyBusy || deleting}
                  data-testid="delete-account-passkey"
                >
                  <KeyRound size={16} aria-hidden="true" style={{ marginRight: 8 }} />
                  {USE_A_PASSKEY}
                </SecondaryAction>
              ))}

            {methods.link &&
              (linkWasSent ? (
                <Callout testId="delete-account-link-sent" tone="plain">
                  {linkSent(email)}
                </Callout>
              ) : linkToken ? null : (
                <SecondaryAction
                  variant="navy-outline"
                  onClick={requestLink}
                  disabled={linkBusy || deleting}
                  data-testid="delete-account-email-link"
                >
                  <Mail size={16} aria-hidden="true" style={{ marginRight: 8 }} />
                  {EMAIL_ME_A_LINK}
                </SecondaryAction>
              ))}

            {methodError && <Callout testId="delete-account-method-error">{methodError}</Callout>}
          </div>
        )}

        {methods.password && (
          <div className="flex flex-col" style={{ gap: 4 }}>
            <Field
              label={PASSWORD_LABEL}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError("");
              }}
              readOnly={deleting}
              help={PASSWORD_HELPER}
              data-testid="delete-account-password"
            />
            {passwordError && <Callout testId="delete-account-password-error">{passwordError}</Callout>}
          </div>
        )}

        {error && <Callout testId="delete-account-error">{error}</Callout>}

        <div className="flex flex-col" style={{ gap: 10 }}>
          <button
            type="button"
            onClick={confirm}
            aria-disabled={!live || undefined}
            data-testid="delete-account-confirm"
            style={verbStyle}
          >
            <Trash2 size={16} aria-hidden="true" />
            {deleting ? DELETE_VERB_BUSY : DELETE_VERB}
          </button>
          <button
            type="button"
            onClick={backToSettings}
            aria-disabled={deleting || undefined}
            data-testid="delete-account-keep"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 48,
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line-strong)",
              color: "var(--nw-navy)",
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 10,
              cursor: deleting ? "not-allowed" : "pointer",
            }}
          >
            {DELETE_KEEP}
          </button>
        </div>
      </div>
    </div>
  );
}
